/**
 * MealPlanEditor Component
 * Main editor for viewing and editing meal plans
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    LLMMealPlanResultOutgoing,
    LLMMealPlanOutgoing,
    OutgoingSearchResult,
} from "../types";
import { MealPlan } from "../services/api";
import { ReactiveNutritionSummary } from "./ReactiveNutritionSummary";
import { ReactiveDayNutritionSummary } from "./ReactiveDayNutritionSummary";
import { SearchResultRow, SearchResultRowRef } from "./SearchResultRow";
import { SaveAsMealPlanModal } from "./SaveAsMealPlanModal";
import { IngredientSearchReplace } from "./IngredientSearchReplace";
import { MealPlanElementActions } from "./MealPlanElementActions";
import { ChangeDescriptionModal } from "./ChangeDescriptionModal";
import { useAuth } from "../contexts/AuthContext";
import { mealPlanApi, UpdateMealPlanData, userApi } from "../services/api";
import { usePassioApi } from "../hooks/usePassioApi";

import {
    simplifyDay,
    simplifyRecipe,
} from "../utils/mealPlanSimplification";
import {
    getMealPreferenceState,
    updateProfilePreferences,
} from "../utils/likesDislikes";
import { Profile, profileApi } from "../services/api";
import { tokenUsageTracker } from "../services/tokenUsageTracker";
import { useToast } from "../contexts/ToastContext";
import { useBackgroundTasks } from "../contexts/BackgroundTaskContext";
import { toTitleCase } from "../utils/textFormatting";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext";

import { hasServiceAccess } from "../utils"; //getUserAccessStatus
import { getUserAccessStatus } from "../utils";
import { APP_CONFIG } from "../config/app.config";

import { getUserApiConfig } from "../utils";
import { 
    NutritionHoverButton, 
    DetailedNutritionTable, 
    ModelInfoContent, 
    ExportContent, 
    ShoppingListContent 
} from "./meal-plan-editor";






interface MealPlanEditorProps {
    mealPlan?: MealPlan | null;
    onSave?: () => void;
    onCancel?: () => void;
}

export const MealPlanEditor: React.FC<MealPlanEditorProps> = ({
    mealPlan,
    onCancel,
    onSave,
}) => {
    const navigate = useNavigate();
    const { dbUser, refreshUser } = useAuth();
    const { showToast } = useToast();
    const { client } = usePassioApi();
    const {
        addTask,
        updateTask,
        getActiveTasksCount,
    } = useBackgroundTasks();
    const {
        setHasUnsavedChanges: setGlobalHasUnsavedChanges,
        showUnsavedChangesModal,
        setShowUnsavedChangesModal,
        confirmNavigation,
        cancelNavigation,
    } = useUnsavedChanges();

    // Check if user has subscription
    const hasAccess = hasServiceAccess(dbUser);
    const accessStatus = getUserAccessStatus(dbUser);
    // Allow manual meal plan building for users who have used free trial (has credit card)
    const canBuildManually = hasAccess || (accessStatus.hasUsedFreeTrial && accessStatus.hasValidPaymentMethod);
    const isPremiumUser = dbUser?.passioapikey || dbUser?.subscription_status === 'premium';

    // Initialize the client state
    const [currentDayIndex, setCurrentDayIndex] = useState(0);
    const [showShoppingList, setShowShoppingList] = useState(false);
    const [showModelInfo, setShowModelInfo] = useState(false);
    const [showExportTab, setShowExportTab] = useState(false);
    const [mealPlanName, setMealPlanName] = useState(
        mealPlan?.name || "New Meal Plan"
    );
    
    // Add detail view toggle state
    const [showDetailView, setShowDetailView] = useState(false);
    
    // Create the initial empty meal plan structure
    const createEmptyMealPlan = (): LLMMealPlanResultOutgoing => ({
        constraints: {
            constraints: {},
            macros: {
                calories: 0,
                protein: 0,
                carbs: 0,
                fat: 0,
                fiber: 0,
            },
        },
        mealPlanDays: [
            {
                breakfast: { recipes: [] },
                lunch: { recipes: [] },
                dinner: { recipes: [] },
                snack: { recipes: [] },
                macros: {
                    calories: 0,
                    protein: 0,
                    carbs: 0,
                    fat: 0,
                    fiber: 0,
                },
            },
        ],
        shoppingList: [],
    });
    
    const [mealPlanData, setMealPlanData] = useState<LLMMealPlanResultOutgoing>(
        () => {
            if (mealPlan?.plan) {
                return mealPlan.plan as LLMMealPlanResultOutgoing;
            }
            return createEmptyMealPlan();
        }
    );

    // Store initial state for comparison
    const [initialMealPlanName, setInitialMealPlanName] = useState(mealPlan?.name || "New Meal Plan");
    const [initialMealPlanData, setInitialMealPlanData] = useState<LLMMealPlanResultOutgoing>(() => {
        if (mealPlan?.plan) {
            return JSON.parse(JSON.stringify(mealPlan.plan));
        }
        return createEmptyMealPlan();
    });
    
    // Effect to handle when mealPlan prop changes (e.g., after SaveAs navigation)
    useEffect(() => {
        if (mealPlan) {
            // Update all state when a new meal plan is loaded
            setMealPlanName(mealPlan.name);
            setMealPlanData(mealPlan.plan as LLMMealPlanResultOutgoing);
            setInitialMealPlanName(mealPlan.name);
            setInitialMealPlanData(JSON.parse(JSON.stringify(mealPlan.plan)));
            // Reset other states
            setHasUnsavedChanges(false);
            setGlobalHasUnsavedChanges(false);
            setSaveError(null);
            setCurrentDayIndex(0); // Reset to first day
            setShowShoppingList(false);
            setShowModelInfo(false);
            setShowExportTab(false);
            // Reset search result row refs
            searchResultRowRefs.current.clear();
            setSearchResultRowKey(prev => prev + 1);
            setIsInitialLoad(true); // Mark as initial load
            setHasUserModifiedQuantities(false); // Reset user modifications flag
            
            // Clear initial load flag after a delay to allow components to render
            setTimeout(() => {
                setIsInitialLoad(false);
            }, 500);
        } else {
            // Reset to empty state when no meal plan
            const emptyPlan = createEmptyMealPlan();
            setMealPlanName("New Meal Plan");
            setMealPlanData(emptyPlan);
            setInitialMealPlanName("New Meal Plan");
            setInitialMealPlanData(emptyPlan);
            setHasUnsavedChanges(false);
            setGlobalHasUnsavedChanges(false);
            setSaveError(null);
            setCurrentDayIndex(0);
            setShowShoppingList(false);
            setShowModelInfo(false);
            setShowExportTab(false);
            searchResultRowRefs.current.clear();
            setSearchResultRowKey(prev => prev + 1);
            setIsInitialLoad(true);
            setHasUserModifiedQuantities(false); // Reset user modifications flag
            
            // Clear initial load flag after a delay
            setTimeout(() => {
                setIsInitialLoad(false);
            }, 500);
        }
    }, [mealPlan, setGlobalHasUnsavedChanges]);

    const [showSaveAsModal, setShowSaveAsModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [hasUserModifiedQuantities, setHasUserModifiedQuantities] = useState(false);

    // State for adding new ingredients
    const [showAddIngredientModal, setShowAddIngredientModal] = useState(false);
    const [addIngredientTarget, setAddIngredientTarget] = useState<{
        dayIndex: number;
        mealType: keyof LLMMealPlanOutgoing;
        recipeIndex: number;
    } | null>(null);

    // State for change description modal
    const [showChangeModal, setShowChangeModal] = useState(false);
    const [changeModalContent, setChangeModalContent] = useState("");
    const [regenerationTarget, setRegenerationTarget] = useState<{
        type: "day" | "meal";
        dayIndex: number;
        mealType?: keyof LLMMealPlanOutgoing;
    } | null>(null);
    const [regeneratingMeals, setRegeneratingMeals] = useState<Set<string>>(
        new Set()
    );
    const [regenerationTaskIds, setRegenerationTaskIds] = useState<
        Map<string, string>
    >(new Map());

    // State for profile data (for likes/dislikes)
    const [associatedProfile, setAssociatedProfile] = useState<Profile | null>(
        null
    );

    // State for shopping list update
    const [isUpdatingShoppingList, setIsUpdatingShoppingList] = useState(false);

    // State for editing recipe names
    const [editingRecipeName, setEditingRecipeName] = useState<{
        dayIndex: number;
        mealType: keyof LLMMealPlanOutgoing;
        recipeIndex: number;
    } | null>(null);
    const [tempRecipeName, setTempRecipeName] = useState("");

    // State for free trial modal
    const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);

    // Navigation state
    const [activeSection, setActiveSection] = useState<string>("");
    const [showNavSidebar, setShowNavSidebar] = useState(window.innerWidth >= 1280);

    // Refs for SearchResultRow components to get updated quantities at save time
    const searchResultRowRefs = useRef<Map<string, SearchResultRowRef>>(new Map());
    
    // State to force re-render of SearchResultRows after save
    const [searchResultRowKey, setSearchResultRowKey] = useState(0);

    // Function to transform meal plan data with updated quantities from SearchResultRow components
    const getMealPlanDataWithUpdatedQuantities = useCallback((): LLMMealPlanResultOutgoing => {
        // Create a deep copy of the current meal plan data
        const transformedData = JSON.parse(JSON.stringify(mealPlanData));
        
        // Iterate through all days, meals, recipes, and ingredients
        transformedData.mealPlanDays.forEach((day: any, dayIndex: number) => {
            const mealTypes = ["breakfast", "lunch", "dinner", "snack"] as const;
            
            mealTypes.forEach((mealType) => {
                const mealSlot = day[mealType] as { recipes: any[] };
                
                if (mealSlot?.recipes) {
                    mealSlot.recipes.forEach((recipe: any, recipeIndex: number) => {
                        if (recipe.ingredients) {
                            recipe.ingredients = recipe.ingredients.map((ingredient: any, ingredientIndex: number) => {
                                // Create a unique key for this ingredient
                                const refKey = `${dayIndex}-${mealType}-${recipeIndex}-${ingredientIndex}`;
                                const rowRef = searchResultRowRefs.current.get(refKey);
                                
                                if (rowRef) {
                                    // Get the ingredient with updated quantity from the SearchResultRow
                                    return rowRef.getResultWithStateQuantity();
                                } else {
                                    // If no ref found (shouldn't happen), return original ingredient
                                    return ingredient;
                                }
                            });
                        }
                    });
                }
            });
        });
        
        return transformedData;
    }, [mealPlanData]);

    const currentDay = mealPlanData.mealPlanDays[currentDayIndex];

    // Navigation structure
    const navigationStructure = React.useMemo(() => {
        const structure: { day: number; meals: { type: string; title: string; id: string; recipeName?: string }[] }[] = [];
        
        mealPlanData.mealPlanDays.forEach((day, dayIndex) => {
            const dayMeals: { type: string; title: string; id: string; recipeName?: string }[] = [];
            
            const mealTypes = ["breakfast", "lunch", "dinner", "snack"] as const;
            mealTypes.forEach((mealType) => {
                const mealSlot = day[mealType] as { recipes: any[] };
                if (mealSlot?.recipes?.length > 0 && mealSlot.recipes[0].name) {
                    dayMeals.push({
                        type: mealType,
                        title: mealType.charAt(0).toUpperCase() + mealType.slice(1),
                        id: `meal-${dayIndex}-${mealType}`,
                        recipeName: toTitleCase(mealSlot.recipes[0].name)
                    });
                }
            });
            
            if (dayMeals.length > 0) {
                structure.push({ day: dayIndex, meals: dayMeals });
            }
        });
        
        return structure;
    }, [mealPlanData.mealPlanDays]);

    // Scroll observer for active section
    React.useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                // Find the most visible section
                let mostVisible = { id: "", ratio: 0 };
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > mostVisible.ratio) {
                        mostVisible = { id: entry.target.id, ratio: entry.intersectionRatio };
                    }
                });
                if (mostVisible.id) {
                    setActiveSection(mostVisible.id);
                }
            },
            { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: "-100px 0px -50% 0px" }
        );

        // Observe all meal sections
        navigationStructure.forEach(({ day, meals }) => {
            if (day === currentDayIndex) {
                meals.forEach(({ id }) => {
                    const element = document.getElementById(id);
                    if (element) observer.observe(element);
                });
            }
        });

        // Also observe the day summary
        const summaryElement = document.getElementById(`day-${currentDayIndex}-summary`);
        if (summaryElement) observer.observe(summaryElement);

        return () => observer.disconnect();
    }, [navigationStructure, currentDayIndex]);

    // Window resize handler
    React.useEffect(() => {
        const handleResize = () => {
            setShowNavSidebar(window.innerWidth >= 1280);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 100; // Offset for fixed header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    // Check if free trial user can submit a new task
    const canFreeTrialUserSubmit = useCallback(() => {
        const accessStatus = getUserAccessStatus(dbUser);
        
        // If user is on free trial (has free runs but no subscription)
        if (accessStatus.hasFreeRuns && !accessStatus.isPremium && !accessStatus.isTrial && !accessStatus.hasApiKey) {
            // Check if there are any active tasks
            const activeTasksCount = getActiveTasksCount();
            return activeTasksCount === 0;
        }
        
        // All other users can submit multiple tasks
        return true;
    }, [dbUser, getActiveTasksCount]);

    // Helper functions to get preference states
    const getDayPreferenceState = (
        dayIndex: number
    ): { isLiked: boolean; isDisliked: boolean } => {
        if (!associatedProfile) return { isLiked: false, isDisliked: false };

        const day = mealPlanData.mealPlanDays[dayIndex];
        const mealTypes: (keyof LLMMealPlanOutgoing)[] = [
            "breakfast",
            "lunch",
            "dinner",
            "snack",
        ];

        let allLiked = true;
        let allDisliked = true;
        let hasRecipes = false;

        for (const mealType of mealTypes) {
            if (mealType === "macros") continue;
            const mealSlot = day[mealType] as { recipes: any[] };
            const recipes = mealSlot?.recipes || [];

            for (const recipe of recipes) {
                hasRecipes = true;
                const state = getMealPreferenceState(
                    recipe.name,
                    associatedProfile.profile_data.likes,
                    associatedProfile.profile_data.dislikes
                );

                if (state !== "liked") allLiked = false;
                if (state !== "disliked") allDisliked = false;
            }
        }

        return {
            isLiked: hasRecipes && allLiked,
            isDisliked: hasRecipes && allDisliked,
        };
    };

    const getMealPreferenceStateForUI = (
        dayIndex: number,
        mealType: keyof LLMMealPlanOutgoing
    ): { isLiked: boolean; isDisliked: boolean } => {
        if (!associatedProfile || mealType === "macros")
            return { isLiked: false, isDisliked: false };

        const day = mealPlanData.mealPlanDays[dayIndex];
        const mealSlot = day[mealType] as { recipes: any[] };
        const recipes = mealSlot?.recipes || [];

        if (recipes.length === 0) return { isLiked: false, isDisliked: false };

        let allLiked = true;
        let allDisliked = true;

        for (const recipe of recipes) {
            const state = getMealPreferenceState(
                recipe.name,
                associatedProfile.profile_data.likes,
                associatedProfile.profile_data.dislikes
            );

            if (state !== "liked") allLiked = false;
            if (state !== "disliked") allDisliked = false;
        }

        return {
            isLiked: allLiked,
            isDisliked: allDisliked,
        };
    };

    // Load associated profile for likes/dislikes
    React.useEffect(() => {
        const loadProfile = async () => {
            if (
                !mealPlan?.profiles ||
                mealPlan.profiles.length === 0 ||
                !dbUser?.id
            ) {
                setAssociatedProfile(null);
                return;
            }

            try {
                // For now, always use the first profile (as mentioned it's always one)
                const profileId = mealPlan.profiles[0];
                const profile = await profileApi.getProfile(
                    dbUser.id,
                    profileId
                );
                setAssociatedProfile(profile);
            } catch (error) {
                console.error("Failed to load profile:", error);
                setAssociatedProfile(null);
            }
        };

        loadProfile();
    }, [mealPlan?.profiles, dbUser?.id]);

    // Note: Nutrition calculations are now handled automatically by reactive components
    // No manual calculation needed - the ReactiveNutritionSummary and ReactiveDayNutritionSummary
    // will automatically calculate from ingredients and update when ingredients change

    // Deep comparison function
    const deepEqual = useCallback((obj1: any, obj2: any): boolean => {
        if (obj1 === obj2) return true;
        if (obj1 == null || obj2 == null) return false;
        if (typeof obj1 !== typeof obj2) return false;

        if (typeof obj1 !== "object") return obj1 === obj2;

        // Handle arrays
        if (Array.isArray(obj1) && Array.isArray(obj2)) {
            if (obj1.length !== obj2.length) {
                console.log("[deepEqual] Array length mismatch:", obj1.length, "vs", obj2.length);
                return false;
            }
            for (let i = 0; i < obj1.length; i++) {
                if (!deepEqual(obj1[i], obj2[i])) {
                    console.log("[deepEqual] Array element mismatch at index", i);
                    return false;
                }
            }
            return true;
        }

        // Handle objects
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) {
            console.log("[deepEqual] Object keys length mismatch:", keys1.length, "vs", keys2.length, "Keys:", keys1, keys2);
            return false;
        }

        for (const key of keys1) {
            if (!keys2.includes(key)) {
                console.log("[deepEqual] Key missing in obj2:", key);
                return false;
            }
            if (!deepEqual(obj1[key], obj2[key])) {
                console.log("[deepEqual] Value mismatch for key:", key);
                return false;
            }
        }

        return true;
    }, []);

    // Effect to check if there are real changes
    useEffect(() => {
        // Skip change detection during initial load
        if (isInitialLoad) {
            console.log("[MealPlanEditor] Skipping change detection - initial load");
            return;
        }
        
        const hasNameChange = mealPlanName !== initialMealPlanName;
        
        // For data changes, check if user has explicitly modified quantities or if the base data changed
        const hasDataChange = hasUserModifiedQuantities || !deepEqual(mealPlanData, initialMealPlanData);
        
        // Debug logging
        if (hasDataChange) {
            console.log("[MealPlanEditor] Data change detected - User modified:", hasUserModifiedQuantities);
        }
        
        const hasActiveRegenerations = regeneratingMeals.size > 0;
        const hasChanges = hasNameChange || hasDataChange;
        const shouldPreventNavigation = hasChanges || hasActiveRegenerations;

        setHasUnsavedChanges(hasChanges);
        setGlobalHasUnsavedChanges(shouldPreventNavigation);
    }, [
        mealPlanName,
        mealPlanData,
        initialMealPlanName,
        initialMealPlanData,
        regeneratingMeals,
        setGlobalHasUnsavedChanges,
        deepEqual,
        isInitialLoad,
        hasUserModifiedQuantities,
    ]);

    // Effect to handle browser unload
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges || regeneratingMeals.size > 0) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedChanges, regeneratingMeals]);

    const handleIngredientQuantityChange = useCallback(
        (
            dayIndex: number,
            mealType: keyof LLMMealPlanOutgoing,
            recipeIndex: number,
            ingredientIndex: number,
            newQuantity: number
        ) => {
            // Mark that user has modified quantities
            setHasUserModifiedQuantities(true);
            
            setMealPlanData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev)); // Deep clone
                const day = newData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                const recipe = mealSlot.recipes[recipeIndex];

                if (
                    recipe &&
                    recipe.ingredients &&
                    recipe.ingredients[ingredientIndex]
                ) {
                    // Store the new quantity in the portion data
                    const ingredient = recipe.ingredients[ingredientIndex];
                    if (ingredient.nutritionPreview?.portion) {
                        // Update the quantity in the portion
                        if (ingredient.nutritionPreview.portion.suggestedQuantity && 
                            ingredient.nutritionPreview.portion.suggestedQuantity.length > 0) {
                            ingredient.nutritionPreview.portion.suggestedQuantity = [newQuantity];
                        } else {
                            ingredient.nutritionPreview.portion.quantity = newQuantity;
                        }
                    }
                }

                return newData;
            });
        },
        []
    );

    const handleIngredientReplace = useCallback(
        (
            dayIndex: number,
            mealType: keyof LLMMealPlanOutgoing,
            recipeIndex: number,
            ingredientIndex: number,
            newIngredient: OutgoingSearchResult
        ) => {
            setMealPlanData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev)); // Deep clone
                const day = newData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                const recipe = mealSlot.recipes[recipeIndex];

                if (
                    recipe &&
                    recipe.ingredients &&
                    recipe.ingredients[ingredientIndex]
                ) {
                    // Replace the ingredient and create new array reference
                    recipe.ingredients = [...recipe.ingredients];
                    recipe.ingredients[ingredientIndex] = newIngredient;
                    // Note: Nutrition totals will be automatically recalculated by reactive components
                }

                return newData;
            });
        },
        []
    );

    const handleIngredientDelete = useCallback(
        (
            dayIndex: number,
            mealType: keyof LLMMealPlanOutgoing,
            recipeIndex: number,
            ingredientIndex: number
        ) => {
            setMealPlanData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev)); // Deep clone
                const day = newData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                const recipe = mealSlot.recipes[recipeIndex];

                if (
                    recipe &&
                    recipe.ingredients &&
                    recipe.ingredients[ingredientIndex]
                ) {
                    // Remove the ingredient and create new array reference
                    recipe.ingredients = recipe.ingredients.filter(
                        (_: any, index: number) => index !== ingredientIndex
                    );
                    // Note: Nutrition totals will be automatically recalculated by reactive components
                }

                return newData;
            });
        },
        []
    );

    const handleAddIngredient = useCallback(
        (
            dayIndex: number,
            mealType: keyof LLMMealPlanOutgoing,
            recipeIndex: number
        ) => {
            setAddIngredientTarget({ dayIndex, mealType, recipeIndex });
            setShowAddIngredientModal(true);
        },
        []
    );

    // Action handlers for meal plan element actions
    const handleRegenerateDayClick = useCallback(
        (dayIndex: number) => {
            const day = mealPlanData.mealPlanDays[dayIndex];
            const dayName = `Day ${dayIndex + 1}`;
            const simplifiedDay = simplifyDay(day, dayName, true);

            // Include constraints in the modal content
            const contentWithConstraints = {
                constraints: mealPlanData.constraints,
                day: simplifiedDay,
            };

            const content = JSON.stringify(contentWithConstraints, null, 2);

            setChangeModalContent(content);
            setRegenerationTarget({ type: "day", dayIndex });
            setShowChangeModal(true);
        },
        [mealPlanData]
    );

    const handleRegenerateMealClick = useCallback(
        (dayIndex: number, mealType: keyof LLMMealPlanOutgoing) => {
            if (mealType === "macros") return;

            const day = mealPlanData.mealPlanDays[dayIndex];
            const mealSlot = day[mealType] as { recipes: any[] };
            const mealName = `${
                mealType.charAt(0).toUpperCase() + mealType.slice(1)
            } - Day ${dayIndex + 1}`;

            // Use the simplifyRecipe function to properly format the meal data
            const simplifiedMeal = {
                meal: mealName,
                recipes: mealSlot.recipes.map(
                    (recipe) => simplifyRecipe(recipe, true) // Include macros
                ),
            };

            // Include constraints in the modal content
            const contentWithConstraints = {
                constraints: mealPlanData.constraints,
                meal: simplifiedMeal,
            };

            const content = JSON.stringify(contentWithConstraints, null, 2);

            setChangeModalContent(content);
            setRegenerationTarget({ type: "meal", dayIndex, mealType });
            setShowChangeModal(true);
        },
        [mealPlanData]
    );

    const handleLikeDayClick = useCallback(
        async (dayIndex: number) => {
            if (!associatedProfile || !dbUser?.id) return;

            try {
                const day = mealPlanData.mealPlanDays[dayIndex];
                const mealTypes: (keyof LLMMealPlanOutgoing)[] = [
                    "breakfast",
                    "lunch",
                    "dinner",
                    "snack",
                ];

                // Like all meals in this day
                let updatedProfile = associatedProfile;
                for (const mealType of mealTypes) {
                    if (mealType === "macros") continue;
                    const mealSlot = day[mealType] as { recipes: any[] };
                    const recipes = mealSlot?.recipes || [];

                    for (const recipe of recipes) {
                        const currentState = getMealPreferenceState(
                            recipe.name,
                            updatedProfile.profile_data.likes,
                            updatedProfile.profile_data.dislikes
                        );

                        if (currentState !== "liked") {
                            updatedProfile = await updateProfilePreferences(
                                dbUser.id,
                                associatedProfile.id,
                                "like",
                                recipe.name,
                                updatedProfile
                            );
                        }
                    }
                }

                setAssociatedProfile(updatedProfile);
            } catch (error) {
                console.error("Failed to like day:", error);
            }
        },
        [associatedProfile, dbUser?.id, mealPlanData]
    );

    const handleDislikeDayClick = useCallback(
        async (dayIndex: number) => {
            if (!associatedProfile || !dbUser?.id) return;

            try {
                const day = mealPlanData.mealPlanDays[dayIndex];
                const mealTypes: (keyof LLMMealPlanOutgoing)[] = [
                    "breakfast",
                    "lunch",
                    "dinner",
                    "snack",
                ];

                // Dislike all meals in this day
                let updatedProfile = associatedProfile;
                for (const mealType of mealTypes) {
                    if (mealType === "macros") continue;
                    const mealSlot = day[mealType] as { recipes: any[] };
                    const recipes = mealSlot?.recipes || [];

                    for (const recipe of recipes) {
                        const currentState = getMealPreferenceState(
                            recipe.name,
                            updatedProfile.profile_data.likes,
                            updatedProfile.profile_data.dislikes
                        );

                        if (currentState !== "disliked") {
                            updatedProfile = await updateProfilePreferences(
                                dbUser.id,
                                associatedProfile.id,
                                "dislike",
                                recipe.name,
                                updatedProfile
                            );
                        }
                    }
                }

                setAssociatedProfile(updatedProfile);
            } catch (error) {
                console.error("Failed to dislike day:", error);
            }
        },
        [associatedProfile, dbUser?.id, mealPlanData]
    );

    const handleLikeMealClick = useCallback(
        async (dayIndex: number, mealType: keyof LLMMealPlanOutgoing) => {
            if (!associatedProfile || !dbUser?.id || mealType === "macros")
                return;

            try {
                const day = mealPlanData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                const recipes = mealSlot?.recipes || [];

                // Check if all recipes are already liked to determine if we should unlike
                const allLiked = recipes.every(
                    (recipe) =>
                        getMealPreferenceState(
                            recipe.name,
                            associatedProfile.profile_data.likes,
                            associatedProfile.profile_data.dislikes
                        ) === "liked"
                );

                // Like/unlike all recipes in this meal
                let updatedProfile = associatedProfile;
                for (const recipe of recipes) {
                    if (allLiked) {
                        // If all are liked, unlike them
                        updatedProfile = await updateProfilePreferences(
                            dbUser.id,
                            associatedProfile.id,
                            "unlike",
                            recipe.name,
                            updatedProfile
                        );
                    } else {
                        // Like them
                        updatedProfile = await updateProfilePreferences(
                            dbUser.id,
                            associatedProfile.id,
                            "like",
                            recipe.name,
                            updatedProfile
                        );
                    }
                }

                setAssociatedProfile(updatedProfile);
            } catch (error) {
                console.error("Failed to like meal:", error);
            }
        },
        [associatedProfile, dbUser?.id, mealPlanData]
    );

    const handleDislikeMealClick = useCallback(
        async (dayIndex: number, mealType: keyof LLMMealPlanOutgoing) => {
            if (!associatedProfile || !dbUser?.id || mealType === "macros")
                return;

            try {
                const day = mealPlanData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                const recipes = mealSlot?.recipes || [];

                // Check if all recipes are already disliked to determine if we should undislike
                const allDisliked = recipes.every(
                    (recipe) =>
                        getMealPreferenceState(
                            recipe.name,
                            associatedProfile.profile_data.likes,
                            associatedProfile.profile_data.dislikes
                        ) === "disliked"
                );

                // Dislike/undislike all recipes in this meal
                let updatedProfile = associatedProfile;
                for (const recipe of recipes) {
                    if (allDisliked) {
                        // If all are disliked, undislike them
                        updatedProfile = await updateProfilePreferences(
                            dbUser.id,
                            associatedProfile.id,
                            "undislike",
                            recipe.name,
                            updatedProfile
                        );
                    } else {
                        // Dislike them
                        updatedProfile = await updateProfilePreferences(
                            dbUser.id,
                            associatedProfile.id,
                            "dislike",
                            recipe.name,
                            updatedProfile
                        );
                    }
                }

                setAssociatedProfile(updatedProfile);
            } catch (error) {
                console.error("Failed to dislike meal:", error);
            }
        },
        [associatedProfile, dbUser?.id, mealPlanData]
    );

    const apiConfig = getUserApiConfig(dbUser);

    const getApiParams = useCallback(() => {
        const params: any = {};

        // Add model parameters from user's API configuration
        if (apiConfig.model) {
          params.model = apiConfig.model;
        }

        if (apiConfig.refinementModel) {
          params.refinementmodel = apiConfig.refinementModel;
        }

        return params;
    }, [apiConfig]);

    const handleChangeModalSubmit = useCallback(
        async (userFeedback: string) => {
            if (!regenerationTarget || !client) {
                console.error("Missing regeneration target or API client");
                return;
            }

            // Check if free trial user can submit
            if (!canFreeTrialUserSubmit()) {
                setShowChangeModal(false);
                setShowFreeTrialModal(true);
                return;
            }

            setShowChangeModal(false);

            // Create a unique key for this regeneration
            const regenerationKey =
                regenerationTarget.type === "day"
                    ? `day_${regenerationTarget.dayIndex}`
                    : `meal_${regenerationTarget.dayIndex}_${regenerationTarget.mealType}`;

            // Add to regenerating meals set
            setRegeneratingMeals((prev) => new Set(prev).add(regenerationKey));

            // Determine target description
            const targetDescription =
                regenerationTarget.type === "day"
                    ? `Day ${regenerationTarget.dayIndex + 1}`
                    : `${regenerationTarget.mealType} on Day ${
                          regenerationTarget.dayIndex + 1
                      }`;

            // Show generation start toast
            showToast({
                message: `Regenerating ${targetDescription}...`,
                type: "info",
                duration: 5000,
            });

            // Add task to background tasks
            const taskId = addTask({
                type: "meal-plan-regeneration",
                status: "running",
                message: `Regenerating ${targetDescription}`,
            });

            // Store task ID for potential cancellation
            setRegenerationTaskIds((prev) =>
                new Map(prev).set(regenerationKey, taskId)
            );

            // Regenerate in background
            const regenerateInBackground = async () => {
                try {
                    // Determine which meals to regenerate
                    let specificMeals: string[] = [];

                    if (regenerationTarget.type === "day") {
                        // Regenerate all meals for the day
                        const mealTypes = [
                            "breakfast",
                            "lunch",
                            "dinner",
                            "snack",
                        ];
                        specificMeals = mealTypes.map(
                            (mealType) =>
                                `day_${regenerationTarget.dayIndex}_${mealType}`
                        );
                    } else if (regenerationTarget.mealType) {
                        // Regenerate specific meal
                        specificMeals = [
                            `day_${regenerationTarget.dayIndex}_${regenerationTarget.mealType}`,
                        ];
                    }

                    const queryParams = getApiParams();

                    // Parse the modal content to get the meal data
                    const modalData = JSON.parse(changeModalContent);

                    // Prepare the content for the API - only constraints and the specific meal/day data
                    const regenerationContent = {
                        constraints: modalData.constraints,
                        ...(modalData.day ? { day: modalData.day } : {}),
                        ...(modalData.meal ? { meal: modalData.meal } : {}),
                        userFeedback: userFeedback.trim() || undefined,
                        specificMeals,
                    };

                    // Decrement free run immediately if user is on free trial
                    if (dbUser && !dbUser.passioapikey && dbUser.free_runs_remaining > 0) {
                        try {
                            await userApi.decrementFreeRun(dbUser.id);
                            // Refresh user data to get updated count
                            await refreshUser();
                        } catch (error) {
                            console.error('Failed to decrement free run:', error);
                            // Continue with regeneration even if decrement fails
                        }
                    }

                    // Call the regenerate meals API with minimal data
                    const regeneratedPlan = await client.regenerateMeals(
                        {
                            mealPlan: regenerationContent, // Send only what's needed
                            specificMeals,
                            userFeedback: userFeedback.trim() || undefined,
                            constraints: modalData.constraints,
                        },
                        queryParams,
                        {
                            ttl: 0, // No caching for regeneration
                        }
                    );

                    // Update only the regenerated meals in the meal plan
                    setMealPlanData((prev) => {
                        const newData = JSON.parse(JSON.stringify(prev)); // Deep clone

                        // Check if we got a valid response with meal plan days
                        if (!regeneratedPlan.mealPlanDays || regeneratedPlan.mealPlanDays.length === 0) {
                            console.error("No meal plan days in regeneration response");
                            return prev;
                        }

                        if (regenerationTarget.type === "day") {
                            // When regenerating a full day, the API returns a single day at index 0
                            // Replace the entire day at the target index
                            const regeneratedDay = regeneratedPlan.mealPlanDays[0];
                            if (regeneratedDay) {
                                newData.mealPlanDays[regenerationTarget.dayIndex] = regeneratedDay;
                            }
                        } else if (regenerationTarget.mealType) {
                            // When regenerating a specific meal, the API returns a single day at index 0
                            // with only the regenerated meal populated
                            const regeneratedDay = regeneratedPlan.mealPlanDays[0];
                            if (regeneratedDay && regeneratedDay[regenerationTarget.mealType]) {
                                const regeneratedMealSlot = regeneratedDay[regenerationTarget.mealType];
                                // Replace only the specific meal in the target day
                                newData.mealPlanDays[regenerationTarget.dayIndex][
                                    regenerationTarget.mealType
                                ] = regeneratedMealSlot;
                            }
                        }

                        // Update shopping list if provided
                        if (regeneratedPlan.shoppingList) {
                            newData.shoppingList = regeneratedPlan.shoppingList;
                        }

                        return newData;
                    });

                    // Check if we have token usage data
                    const usageData = tokenUsageTracker.getUsageData();

                    // Update task status
                    updateTask(taskId, {
                        status: "completed",
                        completedAt: new Date(),
                        tokenUsage: usageData?.requestUsage,
                    });

                    // Show success toast
                    showToast({
                        message: `${targetDescription} regenerated successfully!`,
                        type: "success",
                        duration: 5000,
                    });

                    if (usageData && usageData.requestUsage > 0) {
                        // Show token usage in a separate toast
                        setTimeout(() => {
                            showToast({
                                message: `Used ${usageData.requestUsage.toLocaleString()} tokens for regeneration`,
                                type: "info",
                                duration: 5000,
                            });
                        }, 500);
                    }
                } catch (err) {
                    console.error("Error regenerating meals:", err);

                    // Update task status
                    updateTask(taskId, {
                        status: "failed",
                        completedAt: new Date(),
                        error:
                            err instanceof Error
                                ? err.message
                                : "Failed to regenerate meals",
                    });

                    // Show error toast
                    showToast({
                        message:
                            err instanceof Error
                                ? err.message
                                : "Failed to regenerate meals",
                        type: "error",
                        duration: 10000,
                    });
                } finally {
                    // Remove from regenerating meals set
                    setRegeneratingMeals((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(regenerationKey);
                        return newSet;
                    });

                    // Remove task ID
                    setRegenerationTaskIds((prev) => {
                        const newMap = new Map(prev);
                        newMap.delete(regenerationKey);
                        return newMap;
                    });

                    setRegenerationTarget(null);
                }
            };

            // Start regeneration in background
            regenerateInBackground();
        },
        [
            regenerationTarget,
            client,
            changeModalContent,
            getApiParams,
            showToast,
            addTask,
            updateTask,
            canFreeTrialUserSubmit,
            dbUser,
            refreshUser,
        ]
    );

    const handleAddIngredientSelect = useCallback(
        (newIngredient: OutgoingSearchResult) => {
            if (!addIngredientTarget) return;

            const { dayIndex, mealType, recipeIndex } = addIngredientTarget;

            // Mark that user has made modifications
            setHasUserModifiedQuantities(true);

            setMealPlanData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev)); // Deep clone
                const day = newData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                const recipe = mealSlot.recipes[recipeIndex];

                if (recipe) {
                    // Initialize ingredients array if it doesn't exist
                    if (!recipe.ingredients) {
                        recipe.ingredients = [];
                    }

                    // Add the new ingredient
                    recipe.ingredients.push(newIngredient);
                }

                return newData;
            });

            // Close modal and reset target
            setShowAddIngredientModal(false);
            setAddIngredientTarget(null);
        },
        [addIngredientTarget]
    );

    const handleAddIngredientCancel = useCallback(() => {
        setShowAddIngredientModal(false);
        setAddIngredientTarget(null);
    }, []);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMealPlanName(e.target.value);
    };

    // Handle Save (update existing meal plan)
    const handleSave = async () => {
        if (!dbUser?.id || !mealPlan?.id) {
            setSaveError("Cannot save: missing user or meal plan information");
            return;
        }

        try {
            setSaving(true);
            setSaveError(null);
            setIsUpdatingShoppingList(true);

            // Get meal plan data with updated quantities from SearchResultRow components
            const dataWithUpdatedQuantities = getMealPlanDataWithUpdatedQuantities();

            // First, update the shopping list
            let updatedMealPlanData = dataWithUpdatedQuantities;
            try {
                if (client && APP_CONFIG.UPDATE_SHOPPING_LIST_ON_SAVE) {
                    // Check for model parameter in URL for manual testing
                    const urlParams = new URLSearchParams(window.location.search);
                    const modelParam = urlParams.get('model');
                    
                    const updatedPlan = await client.updateMealPlanShoppingList(
                        dataWithUpdatedQuantities,
                        undefined, // cacheOptions
                        modelParam // optional model parameter
                    );
                    updatedMealPlanData = updatedPlan;
                    // Note: We don't update the state here to preserve the original data for calculations
                }
            } catch (error) {
                console.error("Error updating shopping list:", error);
                // Continue with save even if shopping list update fails
                showToast({
                    message:
                        "Warning: Shopping list update failed, saving without update",
                    type: "warning",
                    duration: 4000,
                });
            } finally {
                setIsUpdatingShoppingList(false);
            }

            const updateData: UpdateMealPlanData = {
                name: mealPlanName,
                plan: updatedMealPlanData,
            };

            await mealPlanApi.updateMealPlan(
                dbUser.id,
                mealPlan.id,
                updateData
            );

            // Update the initial state to reflect the saved changes
            setInitialMealPlanName(mealPlanName);
            setInitialMealPlanData(JSON.parse(JSON.stringify(updatedMealPlanData)));
            
            // Update the current meal plan data to the saved version
            setMealPlanData(updatedMealPlanData);
            
            // Clear unsaved changes flag
            setHasUnsavedChanges(false);
            setGlobalHasUnsavedChanges(false);
            setIsInitialLoad(true); // Temporarily set initial load to prevent false positive
            setHasUserModifiedQuantities(false); // Reset user modifications flag
            
            // Show success toast
            showToast({
                message: "Meal plan saved successfully!",
                type: "success",
                duration: 3000,
            });
            
            // Force re-render of SearchResultRows to sync their internal state
            setSearchResultRowKey(prev => prev + 1);
            
            // Clear initial load flag and force a re-check of unsaved changes state
            // This ensures the UI updates properly after save
            setTimeout(() => {
                setIsInitialLoad(false);
                const currentDataWithQuantities = getMealPlanDataWithUpdatedQuantities();
                const hasChanges = !deepEqual(currentDataWithQuantities, updatedMealPlanData);
                setHasUnsavedChanges(hasChanges);
                setGlobalHasUnsavedChanges(hasChanges);
            }, 100);

            // Don't call the parent's onSave callback - we want to stay on the editor
            // onSave?.();
        } catch (error) {
            console.error("Error saving meal plan:", error);
            setSaveError(
                error instanceof Error
                    ? error.message
                    : "Failed to save meal plan"
            );
            showToast({
                message:
                    error instanceof Error
                        ? error.message
                        : "Failed to save meal plan",
                type: "error",
                duration: 5000,
            });
        } finally {
            setSaving(false);
            setIsUpdatingShoppingList(false);
        }
    };

    // Handle Save As success
    const handleSaveAsSuccess = (savedMealPlan: MealPlan) => {
        setShowSaveAsModal(false);
        
        // Navigate to the new meal plan in the editor
        navigate(`/meal-planner/editor?id=${savedMealPlan.id}`, { replace: true });
        
        // Show success toast
        showToast({
            message: "Meal plan saved successfully!",
            type: "success",
            duration: 3000,
        });
    };

    const handleNavigationWithWarning = useCallback(
        (navigationFn: () => void) => {
            const hasActiveRegenerations = regeneratingMeals.size > 0;

            if (hasUnsavedChanges || hasActiveRegenerations) {
                setShowUnsavedChangesModal(true);
            } else {
                navigationFn();
            }
        },
        [hasUnsavedChanges, regeneratingMeals, setShowUnsavedChangesModal]
    );

    const handleCancelClick = () => {
        handleNavigationWithWarning(() => {
            onCancel?.();
        });
    };

    const renderMealTimeSection = (
        mealType: keyof LLMMealPlanOutgoing,
        title: string
    ) => {
        if (mealType === "macros") return null; // Skip macros field

        const mealTimeSlot = currentDay[mealType] as { recipes: any[] };
        const recipes = mealTimeSlot?.recipes || [];

        // Check if this meal is being regenerated
        const mealKey = `meal_${currentDayIndex}_${mealType}`;
        const dayKey = `day_${currentDayIndex}`;
        const isRegenerating =
            regeneratingMeals.has(mealKey) || regeneratingMeals.has(dayKey);

        return (
            <div
                key={mealType}
                id={`day-${currentDayIndex}-${mealType}`}
                style={{
                    ...mealSectionStyle,
                    opacity: isRegenerating ? 0.6 : 1,
                    pointerEvents: isRegenerating ? "none" : "auto",
                    position: "relative",
                }}
            >
                {isRegenerating && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "12px",
                            zIndex: 10,
                        }}
                    >
                        <div
                            style={{
                                textAlign: "center",
                                padding: "20px",
                            }}
                        >
                            <div
                                style={{
                                    display: "inline-block",
                                    width: "40px",
                                    height: "40px",
                                    border: "3px solid #f3f4f6",
                                    borderRadius: "50%",
                                    borderTopColor: "#3b82f6",
                                    animation: "spin 1s linear infinite",
                                    marginBottom: "12px",
                                }}
                            />
                            <div
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#374151",
                                }}
                            >
                                Regenerating {title}...
                            </div>
                        </div>
                    </div>
                )}

                <div style={mealTitleStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <h3 style={mealNameStyle}>
                            {title}
                        </h3>
                        <NutritionHoverButton
                            title={`${title} - Day ${currentDayIndex + 1}`}
                            dayIndex={currentDayIndex}
                            mealType={mealType}
                            searchResultRowRefs={searchResultRowRefs}
                            client={client}
                            showDetailView={showDetailView}
                            mealPlanData={mealPlanData}
                        />
                    </div>
                    <MealPlanElementActions
                        onRefresh={() =>
                            handleRegenerateMealClick(currentDayIndex, mealType)
                        }
                        onLike={() =>
                            handleLikeMealClick(currentDayIndex, mealType)
                        }
                        onDislike={() =>
                            handleDislikeMealClick(currentDayIndex, mealType)
                        }
                        refreshTooltip="Regenerate Meal"
                        likeTooltip="Like this meal"
                        dislikeTooltip="Dislike this meal"
                        size="small"
                        variant="subtle"
                        isLiked={
                            getMealPreferenceStateForUI(
                                currentDayIndex,
                                mealType
                            ).isLiked
                        }
                        isDisliked={
                            getMealPreferenceStateForUI(
                                currentDayIndex,
                                mealType
                            ).isDisliked
                        }
                        refreshDisabled={!isPremiumUser}
                    />
                </div>

                <div style={mealContentStyle}>
                    {recipes.length === 0 ? (
                        <div>
                            <div style={emptyMealStyle}>
                                <div style={emptyMealIconStyle}>🍽️</div>
                                <div>No recipes in {title.toLowerCase()}</div>
                                <div style={emptyMealSubtextStyle}>
                                    {canBuildManually 
                                        ? "Click 'Add Ingredient' below to start building your meal"
                                        : "Recipes will appear here when meal plan is generated"}
                                </div>
                            </div>
                            {/* Show Add Ingredient button for empty meals when user can build manually */}
                            {canBuildManually && (
                                <button
                                    onClick={() => {
                                        // Create a default recipe when adding to empty meal
                                        setMealPlanData((prev) => {
                                            const newData = JSON.parse(JSON.stringify(prev));
                                            const day = newData.mealPlanDays[currentDayIndex];
                                            const mealSlot = day[mealType] as { recipes: any[] };
                                            
                                            // Create a new empty recipe
                                            if (!mealSlot.recipes) {
                                                mealSlot.recipes = [];
                                            }
                                            mealSlot.recipes.push({
                                                name: `Custom ${title}`,
                                                ingredients: [],
                                                preparation: ""
                                            });
                                            
                                            return newData;
                                        });
                                        
                                        // Then open the add ingredient modal for the new recipe
                                        setTimeout(() => {
                                            handleAddIngredient(currentDayIndex, mealType, 0);
                                        }, 100);
                                    }}
                                    style={{
                                        ...addIngredientButtonStyle,
                                        marginTop: "24px",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                        maxWidth: "300px",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#f0f9ff";
                                        e.currentTarget.style.borderColor = "#3b82f6";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "#fafbfc";
                                        e.currentTarget.style.borderColor = "#d1d5db";
                                    }}
                                >
                                    <span style={{ marginRight: "8px" }}>➕</span>
                                    Add Ingredient
                                </button>
                            )}
                        </div>
                    ) : (
                        <div style={recipesContainerStyle}>
                            {recipes.map((recipe, recipeIndex) => (
                                <div
                                    key={recipeIndex}
                                    style={recipeContainerStyle}
                                >
                                    <div style={recipeHeaderStyle}>
                                        {editingRecipeName?.dayIndex === currentDayIndex &&
                                        editingRecipeName?.mealType === mealType &&
                                        editingRecipeName?.recipeIndex === recipeIndex ? (
                                            <input
                                                type="text"
                                                value={tempRecipeName}
                                                onChange={(e) => setTempRecipeName(e.target.value)}
                                                onBlur={() => {
                                                    handleRecipeNameChange(
                                                        currentDayIndex,
                                                        mealType,
                                                        recipeIndex,
                                                        tempRecipeName
                                                    );
                                                    setEditingRecipeName(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleRecipeNameChange(
                                                            currentDayIndex,
                                                            mealType,
                                                            recipeIndex,
                                                            tempRecipeName
                                                        );
                                                        setEditingRecipeName(null);
                                                    } else if (e.key === 'Escape') {
                                                        setEditingRecipeName(null);
                                                    }
                                                }}
                                                autoFocus
                                                style={{
                                                    ...recipeNameStyle,
                                                    border: "2px solid #3b82f6",
                                                    borderRadius: "4px",
                                                    padding: "4px 8px",
                                                    backgroundColor: "white",
                                                    outline: "none",
                                                }}
                                            />
                                        ) : (
                                            <h4 
                                                style={{
                                                    ...recipeNameStyle,
                                                    cursor: "pointer",
                                                    padding: "4px 8px",
                                                    borderRadius: "4px",
                                                    transition: "background-color 0.2s",
                                                }}
                                                onClick={() => {
                                                    setEditingRecipeName({
                                                        dayIndex: currentDayIndex,
                                                        mealType,
                                                        recipeIndex
                                                    });
                                                    setTempRecipeName(recipe.name);
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = "transparent";
                                                }}
                                                title="Click to edit recipe name"
                                            >
                                                {toTitleCase(recipe.name)}
                                            </h4>
                                        )}
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <NutritionHoverButton
                                                title={toTitleCase(recipe.name)}
                                                dayIndex={currentDayIndex}
                                                mealType={mealType}
                                                recipeIndex={recipeIndex}
                                                searchResultRowRefs={searchResultRowRefs}
                                                client={client}
                                                showDetailView={showDetailView}
                                                mealPlanData={mealPlanData}
                                                style={{ marginRight: "8px" }}
                                            />
                                        </div>
                                    </div>

                                    {/* Recipe-level nutrition summary */}
                                    <ReactiveNutritionSummary
                                        ingredients={recipe.ingredients || []}
                                        title="Recipe Nutrition"
                                        itemLabel="ingredients"
                                        variant="compact"
                                        style={recipeNutritionStyle}
                                    />

                                    {/* Recipe ingredients */}
                                    <div style={ingredientsContainerStyle}>
                                        <h5 style={ingredientsSectionTitle}>
                                            Ingredients
                                        </h5>
                                        {recipe.ingredients?.length > 0 ? (
                                            <div style={ingredientsListStyle}>
                                                {recipe.ingredients.map(
                                                    (
                                                        ingredient: OutgoingSearchResult,
                                                        ingredientIndex: number
                                                    ) => {
                                                        const refKey = `${currentDayIndex}-${mealType}-${recipeIndex}-${ingredientIndex}`;
                                                        return (
                                                            <SearchResultRow
                                                                key={`${ingredient.resultId}-${ingredientIndex}-${searchResultRowKey}`}
                                                                ref={(ref) => {
                                                                    if (ref) {
                                                                        searchResultRowRefs.current.set(refKey, ref);
                                                                    } else {
                                                                        searchResultRowRefs.current.delete(refKey);
                                                                    }
                                                                }}
                                                                searchResult={
                                                                    ingredient
                                                                }
                                                                onQuantityChange={(
                                                                    newQuantity
                                                                ) =>
                                                                    handleIngredientQuantityChange(
                                                                        currentDayIndex,
                                                                        mealType,
                                                                        recipeIndex,
                                                                        ingredientIndex,
                                                                        newQuantity
                                                                    )
                                                                }
                                                                onReplace={(
                                                                    newResult
                                                                ) =>
                                                                    handleIngredientReplace(
                                                                        currentDayIndex,
                                                                        mealType,
                                                                        recipeIndex,
                                                                        ingredientIndex,
                                                                        newResult
                                                                    )
                                                                }
                                                                onDelete={() =>
                                                                    handleIngredientDelete(
                                                                        currentDayIndex,
                                                                        mealType,
                                                                        recipeIndex,
                                                                        ingredientIndex
                                                                    )
                                                                }
                                                                showDetailedNutrients={showDetailView}
                                                            />
                                                        );
                                                    }
                                                )}
                                            </div>
                                        ) : (
                                            <div style={emptyIngredientsStyle}>
                                                No ingredients in this recipe
                                            </div>
                                        )}

                                        {/* Add Ingredient Button */}
                                        <button
                                            onClick={() =>
                                                handleAddIngredient(
                                                    currentDayIndex,
                                                    mealType,
                                                    recipeIndex
                                                )
                                            }
                                            style={addIngredientButtonStyle}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    "#f0f9ff";
                                                e.currentTarget.style.borderColor =
                                                    "#3b82f6";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    "#fafbfc";
                                                e.currentTarget.style.borderColor =
                                                    "#d1d5db";
                                            }}
                                        >
                                            <span
                                                style={{ marginRight: "8px" }}
                                            >
                                                ➕
                                            </span>
                                            Add Ingredient
                                        </button>
                                    </div>

                                    {recipe.preparation && (
                                        <div style={preparationContainerStyle}>
                                            <h5 style={preparationTitleStyle}>
                                                Preparation
                                            </h5>
                                            <div style={preparationTextStyle}>
                                                {recipe.preparation}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            
                            {/* Add Recipe Button - show when there's at least one recipe */}
                            {recipes.length > 0 && canBuildManually && (
                                <button
                                    onClick={() => handleAddRecipe(currentDayIndex, mealType, title)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "16px",
                                        border: "2px dashed #d1d5db",
                                        borderRadius: "8px",
                                        backgroundColor: "#fafbfc",
                                        color: "#6b7280",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        transition: "all 0.2s",
                                        width: "100%",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#f0f9ff";
                                        e.currentTarget.style.borderColor = "#3b82f6";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "#fafbfc";
                                        e.currentTarget.style.borderColor = "#d1d5db";
                                    }}
                                >
                                    <span style={{ marginRight: "8px" }}>➕</span>
                                    Add Another Recipe
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Styles
    const containerStyle: React.CSSProperties = {
        backgroundColor: "#f8fafc",
        borderRadius: "16px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        padding: "0",
        minHeight: "600px",
        overflow: "hidden",
    };

    const headerStyle: React.CSSProperties = {
        backgroundColor: "white",
        paddingTop: "24px",
        paddingLeft: "32px",
        paddingRight: "32px",
        paddingBottom: "0",
        borderBottom: "1px solid #e5e7eb",
    };

    const titleContainerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
    };

    const nameInputStyle: React.CSSProperties = {
        fontSize: "24px",
        fontWeight: "600",
        color: "#1a202c",
        border: "2px solid transparent",
        borderRadius: "8px",
        padding: "8px 12px",
        backgroundColor: "#f9fafb",
        outline: "none",
        transition: "all 0.2s",
        flex: 1,
        marginRight: "16px",
    };

    const actionButtonsStyle: React.CSSProperties = {
        display: "flex",
        gap: "12px",
    };

    const cancelButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        backgroundColor: "white",
        color: "#374151",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s",
    };

    const saveButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "none",
        borderRadius: "8px",
        backgroundColor: "#3b82f6",
        color: "white",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    };

    const disabledButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "none",
        borderRadius: "8px",
        backgroundColor: "#9ca3af",
        color: "white",
        cursor: "not-allowed",
        fontSize: "14px",
        fontWeight: "500",
    };

    const saveAsButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        backgroundColor: "white",
        color: "#374151",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s",
    };

    const dayTabsStyle: React.CSSProperties = {
        display: "flex",
        gap: "8px",
        paddingBottom: "0",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
    };

    const dayTabStyle: React.CSSProperties = {
        padding: "12px 20px",
        border: "none",
        backgroundColor: "transparent",
        color: "#6b7280",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        borderBottom: "3px solid transparent",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
    };

    const activeTabStyle: React.CSSProperties = {
        ...dayTabStyle,
        color: "#3b82f6",
        borderBottomColor: "#3b82f6",
        backgroundColor: "#eff6ff",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
    };

    const dayContentStyle: React.CSSProperties = {
        padding: "32px",
        backgroundColor: "#f8fafc",
        minHeight: "500px",
    };

    const dayHeaderStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
        padding: "20px 24px",
        background: "linear-gradient(to right, #ffffff, #f0f9ff)",
        borderRadius: "12px",
        border: "1px solid #e0e7ff",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    };

    const dayTitleStyle: React.CSSProperties = {
        fontSize: "22px",
        fontWeight: "600",
        color: "#1e293b",
        display: "flex",
        alignItems: "center",
        gap: "12px",
    };

    const dayNutritionStyle: React.CSSProperties = {
        marginBottom: "32px",
    };

    const mealsGridStyle: React.CSSProperties = {
        display: "grid",
        gap: "24px",
    };

    const mealSectionStyle: React.CSSProperties = {
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
        transition: "all 0.2s",
    };

    const mealTitleStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 24px",
        background: "linear-gradient(to bottom, rgba(200, 255, 200, 0.3) 0%, #ffffff 80%)", // gradient from soft green to white
        borderBottom: "none",
    };

    const mealNameStyle: React.CSSProperties = {
        fontSize: "20px",
        fontWeight: "600",
        color: "#1e293b",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        letterSpacing: "0.025em",
    };

    const mealContentStyle: React.CSSProperties = {
        padding: "20px",
    };

    const emptyMealStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "40px 20px",
        color: "#9ca3af",
    };

    const emptyMealIconStyle: React.CSSProperties = {
        fontSize: "48px",
        marginBottom: "12px",
        opacity: 0.5,
    };

    const emptyMealSubtextStyle: React.CSSProperties = {
        fontSize: "13px",
        marginTop: "8px",
        color: "#6b7280",
    };

    const recipesContainerStyle: React.CSSProperties = {
        display: "grid",
        gap: "20px",
    };

    const recipeContainerStyle: React.CSSProperties = {
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        padding: "20px",
        backgroundColor: "#fafbfc",
        transition: "all 0.2s",
    };

    const recipeHeaderStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
    };

    const recipeNameStyle: React.CSSProperties = {
        fontSize: "16px",
        fontWeight: "600",
        color: "#1e293b",
        margin: 0,
    };

    const recipeNutritionStyle: React.CSSProperties = {
        marginBottom: "20px",
        padding: "16px",
        backgroundColor: "#f0f9ff",
        borderRadius: "8px",
        border: "1px solid #bae6fd",
    };

    const ingredientsContainerStyle: React.CSSProperties = {
        marginBottom: "16px",
    };

    const ingredientsSectionTitle: React.CSSProperties = {
        fontSize: "13px",
        fontWeight: "600",
        color: "#64748b",
        marginBottom: "12px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    };

    const ingredientsListStyle: React.CSSProperties = {
        display: "grid",
        gap: "8px",
    };

    const emptyIngredientsStyle: React.CSSProperties = {
        color: "#9ca3af",
        fontSize: "14px",
        fontStyle: "italic",
        padding: "24px",
        textAlign: "center",
        backgroundColor: "#f9fafb",
        borderRadius: "8px",
        border: "1px dashed #e2e8f0",
    };

    const addIngredientButtonStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "12px 16px",
        border: "2px dashed #cbd5e1",
        borderRadius: "8px",
        backgroundColor: "#f8fafc",
        color: "#64748b",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s",
        marginTop: "12px",
        width: "100%",
    };

    const preparationContainerStyle: React.CSSProperties = {
        marginTop: "20px",
        paddingTop: "20px",
        borderTop: "1px solid #e2e8f0",
    };

    const preparationTitleStyle: React.CSSProperties = {
        fontSize: "13px",
        fontWeight: "600",
        color: "#64748b",
        marginBottom: "10px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    };

    const preparationTextStyle: React.CSSProperties = {
        fontSize: "14px",
        color: "#4b5563",
        lineHeight: "1.6",
        padding: "12px",
        backgroundColor: "#f9fafb",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
    };

    const errorDisplayStyle: React.CSSProperties = {
        marginTop: "16px",
        padding: "12px 16px",
        border: "1px solid #fecaca",
        borderRadius: "8px",
        backgroundColor: "#fef2f2",
        color: "#dc2626",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };

    const handleRecipeNameChange = useCallback(
        (
            dayIndex: number,
            mealType: keyof LLMMealPlanOutgoing,
            recipeIndex: number,
            newName: string
        ) => {
            setMealPlanData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const day = newData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                const recipe = mealSlot.recipes[recipeIndex];

                if (recipe) {
                    recipe.name = newName;
                }

                return newData;
            });
        },
        []
    );

    const handleAddRecipe = useCallback(
        (
            dayIndex: number,
            mealType: keyof LLMMealPlanOutgoing,
            title: string
        ) => {
            setMealPlanData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const day = newData.mealPlanDays[dayIndex];
                const mealSlot = day[mealType] as { recipes: any[] };
                
                // Create a new empty recipe
                if (!mealSlot.recipes) {
                    mealSlot.recipes = [];
                }
                const recipeNumber = mealSlot.recipes.length + 1;
                mealSlot.recipes.push({
                    name: `${title} Recipe ${recipeNumber}`,
                    ingredients: [],
                    preparation: ""
                });
                
                return newData;
            });
        },
        []
    );

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div style={titleContainerStyle}>
                    <input
                        type="text"
                        value={mealPlanName}
                        onChange={handleNameChange}
                        style={nameInputStyle}
                        onFocus={(e) => {
                            e.target.style.borderColor = "#3b82f6";
                            e.target.style.backgroundColor = "white";
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = "transparent";
                            e.target.style.backgroundColor = "#f9fafb";
                        }}
                    />
                    <div style={actionButtonsStyle}>
                        {onCancel && (
                            <button
                                onClick={handleCancelClick}
                                style={cancelButtonStyle}
                            >
                                Cancel
                            </button>
                        )}
                        
                        {/* Show Save As only if this is an existing meal plan */}
                        {mealPlan && (
                            <button
                                onClick={() => setShowSaveAsModal(true)}
                                style={saveAsButtonStyle}
                                disabled={saving}
                                onMouseEnter={(e) => {
                                    if (!saving) {
                                        e.currentTarget.style.backgroundColor =
                                            "#f3f4f6";
                                        e.currentTarget.style.borderColor =
                                            "#9ca3af";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!saving) {
                                        e.currentTarget.style.backgroundColor =
                                            "white";
                                        e.currentTarget.style.borderColor =
                                            "#d1d5db";
                                    }
                                }}
                            >
                                Save As...
                            </button>
                        )}
                        
                        {/* Show Save button for existing meal plans with changes, or Save Plan for new meal plans */}
                        {mealPlan ? (
                            // Existing meal plan - show Save only if there are changes
                            onSave && (
                                <button
                                    onClick={handleSave}
                                    disabled={saving || !hasUnsavedChanges}
                                    style={
                                        saving || !hasUnsavedChanges
                                            ? disabledButtonStyle
                                            : saveButtonStyle
                                    }
                                >
                                    {saving ? "Saving..." : "Save"}
                                </button>
                            )
                        ) : (
                            // New meal plan - always show Save Plan button
                            <button
                                onClick={() => setShowSaveAsModal(true)}
                                style={saveButtonStyle}
                                disabled={saving}
                                onMouseEnter={(e) => {
                                    if (!saving) {
                                        e.currentTarget.style.backgroundColor = "#2563eb";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!saving) {
                                        e.currentTarget.style.backgroundColor = "#3b82f6";
                                    }
                                }}
                            >
                                {saving ? "Saving..." : "Save Plan"}
                            </button>
                        )}
                    </div>
                </div>

                {/* Save Error Display */}
                {saveError && (
                    <div style={errorDisplayStyle}>
                        <span>⚠️</span>
                        <div>
                            <strong>Save Error:</strong> {saveError}
                        </div>
                    </div>
                )}

                {/* Day Tabs */}
                <div style={dayTabsStyle}>
                    {mealPlanData.mealPlanDays.map((_, dayIndex) => (
                        <button
                            key={dayIndex}
                            onClick={() => {
                                setCurrentDayIndex(dayIndex);
                                setShowShoppingList(false);
                                setShowModelInfo(false);
                                setShowExportTab(false);
                            }}
                            style={
                                dayIndex === currentDayIndex &&
                                !showShoppingList &&
                                !showModelInfo &&
                                !showExportTab
                                    ? activeTabStyle
                                    : dayTabStyle
                            }
                        >
                            Day {dayIndex + 1}
                        </button>
                    ))}
                    
                    {/* Add Day Button - only show when user has subscription */}
                    {hasAccess && (
                        <button
                            key="add-day"
                            onClick={() => {
                                setMealPlanData((prev) => {
                                    const newData = JSON.parse(JSON.stringify(prev));
                                    // Add a new empty day
                                    newData.mealPlanDays.push({
                                        breakfast: { recipes: [] },
                                        lunch: { recipes: [] },
                                        dinner: { recipes: [] },
                                        snack: { recipes: [] },
                                        macros: {
                                            calories: 0,
                                            protein: 0,
                                            carbs: 0,
                                            fat: 0,
                                            fiber: 0,
                                        },
                                    });
                                    return newData;
                                });
                                // Switch to the new day
                                setCurrentDayIndex(mealPlanData.mealPlanDays.length);
                                setShowShoppingList(false);
                                setShowModelInfo(false);
                                setShowExportTab(false);
                            }}
                            style={{
                                ...dayTabStyle,
                                borderStyle: "dashed",
                                borderWidth: "2px",
                                borderColor: "#d1d5db",
                                backgroundColor: "transparent",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f0f9ff";
                                e.currentTarget.style.borderColor = "#3b82f6";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.borderColor = "#d1d5db";
                            }}
                        >
                            <span style={{ marginRight: "4px" }}>➕</span>
                            Add Day
                        </button>
                    )}
                    
                    {/* Shopping List Tab - Always show */}
                    <button
                        key="shopping-list"
                        onClick={() => {
                            setShowShoppingList(true);
                            setShowModelInfo(false);
                            setShowExportTab(false);
                        }}
                        style={
                            showShoppingList && !showModelInfo && !showExportTab
                                ? activeTabStyle
                                : dayTabStyle
                        }
                    >
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            🛒 Shopping List
                            {hasUnsavedChanges && (
                                <span
                                    style={{
                                        backgroundColor: "#f59e0b",
                                        color: "white",
                                        fontSize: "10px",
                                        fontWeight: "600",
                                        padding: "2px 6px",
                                        borderRadius: "10px",
                                        minWidth: "16px",
                                        height: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    !
                                </span>
                            )}
                        </span>
                    </button>
                    
                    {mealPlan?.generation_prompt && (
                        <button
                            key="model-info"
                            onClick={() => {
                                setShowModelInfo(true);
                                setShowShoppingList(false);
                                setShowExportTab(false);
                            }}
                            style={
                                showModelInfo && !showShoppingList && !showExportTab
                                    ? activeTabStyle
                                    : dayTabStyle
                            }
                        >
                            🤖 Model Info
                        </button>
                    )}
                    
                    {/* Export/Download Tab - Always show as last tab */}
                    <button
                        key="export-download"
                        onClick={() => {
                            setShowExportTab(true);
                            setShowShoppingList(false);
                            setShowModelInfo(false);
                        }}
                        style={
                            showExportTab
                                ? activeTabStyle
                                : dayTabStyle
                        }
                    >
                        📥 Export/Download
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div style={dayContentStyle}>
                {showExportTab ? (
                    <ExportContent
                        mealPlanData={getMealPlanDataWithUpdatedQuantities()}
                        mealPlanName={mealPlanName}
                        hasUnsavedChanges={hasUnsavedChanges}
                    />
                ) : showModelInfo && mealPlan ? (
                    <ModelInfoContent
                        mealPlan={mealPlan}
                        mealPlanData={mealPlanData}
                    />
                ) : showShoppingList ? (
                    <ShoppingListContent
                        shoppingList={mealPlanData.shoppingList || []}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onRegenerateShoppingList={async () => {
                            try {
                                setIsUpdatingShoppingList(true);
                                if (client) {
                                                                            // Check for model parameter in URL for manual testing
                                        const urlParams = new URLSearchParams(window.location.search);
                                        const modelParam = urlParams.get('model');
                                        
                                        // Use the transformed data with updated quantities
                                        const dataWithUpdatedQuantities = getMealPlanDataWithUpdatedQuantities();
                                        
                                        const updatedPlan =
                                            await client.updateMealPlanShoppingList(
                                                dataWithUpdatedQuantities,
                                                undefined, // cacheOptions
                                                modelParam // optional model parameter
                                            );
                                        setMealPlanData(updatedPlan);
                                        
                                        // Force re-render of SearchResultRows after shopping list update
                                        setSearchResultRowKey(prev => prev + 1);
                                        
                                        showToast({
                                            message:
                                                "Shopping list updated successfully!",
                                            type: "success",
                                            duration: 3000,
                                        });
                                    } else {
                                        throw new Error("API client not initialized");
                                    }
                                } catch (error) {
                                    console.error(
                                        "Error updating shopping list:",
                                        error
                                    );
                                    showToast({
                                        message: "Failed to update shopping list",
                                        type: "error",
                                        duration: 4000,
                                    });
                                } finally {
                                    setIsUpdatingShoppingList(false);
                                }
                            }}
                    />
                ) : (
                    <>
                        {/* Day Header */}
                        <div style={dayHeaderStyle}>
                            <div style={dayTitleStyle}>
                                Day {currentDayIndex + 1}
                                <span style={{ 
                                    fontSize: "14px", 
                                    color: "#64748b",
                                    fontWeight: "normal",
                                    marginLeft: "auto",
                                }}>
                                    {new Date(Date.now() + currentDayIndex * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                                </span>
                            </div>
                            <MealPlanElementActions
                                onRefresh={() =>
                                    handleRegenerateDayClick(currentDayIndex)
                                }
                                onLike={() =>
                                    handleLikeDayClick(currentDayIndex)
                                }
                                onDislike={() =>
                                    handleDislikeDayClick(currentDayIndex)
                                }
                                refreshTooltip="Regenerate Meal Plan Day"
                                likeTooltip="Like this day's meal plan"
                                dislikeTooltip="Dislike this day's meal plan"
                                size="medium"
                                variant="default"
                                isLiked={
                                    getDayPreferenceState(currentDayIndex)
                                        .isLiked
                                }
                                isDisliked={
                                    getDayPreferenceState(currentDayIndex)
                                        .isDisliked
                                }
                                refreshDisabled={!isPremiumUser}
                            />
                        </div>

                        {/* Check if entire day is being regenerated */}
                        {regeneratingMeals.has(`day_${currentDayIndex}`) ? (
                            <div
                                style={{
                                    position: "relative",
                                    minHeight: "400px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "8px",
                                    border: "1px solid #e5e7eb",
                                    marginTop: "20px",
                                }}
                            >
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "40px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "inline-block",
                                            width: "60px",
                                            height: "60px",
                                            border: "4px solid #f3f4f6",
                                            borderRadius: "50%",
                                            borderTopColor: "#3b82f6",
                                            animation:
                                                "spin 1s linear infinite",
                                            marginBottom: "20px",
                                        }}
                                    />
                                    <h3
                                        style={{
                                            fontSize: "18px",
                                            fontWeight: "600",
                                            color: "#374151",
                                            marginBottom: "8px",
                                        }}
                                    >
                                        Regenerating Day {currentDayIndex + 1}
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: "14px",
                                            color: "#6b7280",
                                        }}
                                    >
                                        Creating new meal options for the entire
                                        day...
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Day-level Nutrition Summary */}
                                <div id={`day-${currentDayIndex}-summary`} style={dayNutritionStyle}>
                                    <ReactiveDayNutritionSummary
                                        dayData={currentDay}
                                        title="Daily Nutrition Total"
                                        variant="standard"
                                    />
                                    
                                    {/* Detail View Toggle */}
                                    <div style={{
                                        marginTop: "16px",
                                        padding: "16px",
                                        backgroundColor: "white",
                                        borderRadius: "8px",
                                        border: "1px solid #e5e7eb",
                                    }}>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}>
                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                            }}>
                                                <span style={{
                                                    fontSize: "14px",
                                                    fontWeight: "500",
                                                    color: "#374151",
                                                }}>
                                                    Detailed Nutrition View
                                                </span>
                                                {!isPremiumUser && (
                                                    <span style={{
                                                        fontSize: "12px",
                                                        color: "#6b7280",
                                                        fontStyle: "italic",
                                                    }}>
                                                        (Premium feature)
                                                    </span>
                                                )}
                                            </div>
                                            <label style={{
                                                position: "relative",
                                                display: "inline-block",
                                                width: "48px",
                                                height: "24px",
                                                cursor: isPremiumUser ? "pointer" : "not-allowed",
                                                opacity: isPremiumUser ? 1 : 0.5,
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={showDetailView}
                                                    onChange={(e) => setShowDetailView(e.target.checked)}
                                                    disabled={!isPremiumUser}
                                                    style={{
                                                        opacity: 0,
                                                        width: 0,
                                                        height: 0,
                                                    }}
                                                />
                                                <span style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: showDetailView ? "#3b82f6" : "#cbd5e1",
                                                    borderRadius: "24px",
                                                    transition: "background-color 0.3s",
                                                }}></span>
                                                <span style={{
                                                    position: "absolute",
                                                    top: "2px",
                                                    left: showDetailView ? "26px" : "2px",
                                                    width: "20px",
                                                    height: "20px",
                                                    backgroundColor: "white",
                                                    borderRadius: "50%",
                                                    transition: "left 0.3s",
                                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                                }}></span>
                                            </label>
                                        </div>
                                    </div>
                                    
                                    {/* Detailed Nutrition Table */}
                                    {showDetailView && isPremiumUser && (
                                        <DetailedNutritionTable 
                                            dayIndex={currentDayIndex}
                                            dayData={currentDay}
                                            searchResultRowRefs={searchResultRowRefs}
                                            client={client}
                                        />
                                    )}
                                </div>

                                {/* Meals Grid */}
                                <div style={mealsGridStyle}>
                                    {renderMealTimeSection(
                                        "breakfast",
                                        "Breakfast"
                                    )}
                                    {renderMealTimeSection("lunch", "Lunch")}
                                    {renderMealTimeSection(
                                        "dinner",
                                        "Dinner"
                                    )}
                                    {renderMealTimeSection(
                                        "snack",
                                        "Snacks"
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Save As Modal */}
            <SaveAsMealPlanModal
                isOpen={showSaveAsModal}
                onClose={() => setShowSaveAsModal(false)}
                onSaveSuccess={handleSaveAsSuccess}
                currentName={mealPlanName}
                mealPlanData={getMealPlanDataWithUpdatedQuantities()}
                originalMealPlan={
                    mealPlan
                        ? {
                              generation_prompt: mealPlan.generation_prompt,
                              generation_url: mealPlan.generation_url,
                              token_count: mealPlan.token_count,
                          }
                        : undefined
                }
            />

            {/* Add Ingredient Modal */}
            <IngredientSearchReplace
                isOpen={showAddIngredientModal}
                currentItem={null} // No current item when adding new ingredient
                onSelect={handleAddIngredientSelect}
                onClose={handleAddIngredientCancel}
            />

            {/* Change Description Modal */}
            <ChangeDescriptionModal
                isOpen={showChangeModal}
                onClose={() => setShowChangeModal(false)}
                onSubmit={handleChangeModalSubmit}
                inputContent={changeModalContent}
                title="Describe Changes"
                placeholder="Describe the changes you want to make..."
            />

            {/* Free Trial Modal */}
            {showFreeTrialModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 10000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            padding: "32px",
                            maxWidth: "450px",
                            width: "90%",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                            animation: "fadeIn 0.2s ease-out",
                        }}
                    >
                        <div style={{ marginBottom: "20px", textAlign: "center" }}>
                            <span style={{ fontSize: "48px" }}>🚦</span>
                        </div>
                        <h3
                            style={{
                                fontSize: "20px",
                                fontWeight: "600",
                                marginBottom: "12px",
                                color: "#1f2937",
                                textAlign: "center",
                            }}
                        >
                            Free Trial Limitation
                        </h3>
                        <p style={{ 
                            color: "#6b7280", 
                            marginBottom: "20px", 
                            lineHeight: "1.6",
                            textAlign: "center" 
                        }}>
                            Free trial users can only run one task at a time. 
                            Please wait for your current task to complete before starting a new one.
                        </p>
                        <p
                            style={{
                                color: "#6b7280",
                                fontSize: "14px",
                                marginBottom: "24px",
                                lineHeight: "1.6",
                                textAlign: "center",
                            }}
                        >
                            To run multiple tasks simultaneously, please subscribe to our premium plan.
                        </p>
                        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                            <button
                                style={{
                                    backgroundColor: "#6b7280",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "10px 24px",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "background-color 0.2s",
                                }}
                                onClick={() => setShowFreeTrialModal(false)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#4b5563";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#6b7280";
                                }}
                            >
                                OK
                            </button>
                            <button
                                style={{
                                    backgroundColor: "#3b82f6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "10px 24px",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "background-color 0.2s",
                                }}
                                onClick={() => {
                                    setShowFreeTrialModal(false);
                                    navigate("/profile?tab=billing");
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#2563eb";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#3b82f6";
                                }}
                            >
                                Upgrade to Premium
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Unsaved Changes Modal */}
            {showUnsavedChangesModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 10000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            padding: "32px",
                            maxWidth: "400px",
                            width: "90%",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        }}
                    >
                        <h3
                            style={{
                                fontSize: "20px",
                                fontWeight: "600",
                                color: "#1f2937",
                                marginBottom: "16px",
                            }}
                        >
                            {regeneratingMeals.size > 0
                                ? "Active Regenerations"
                                : "Unsaved Changes"}
                        </h3>

                        {regeneratingMeals.size > 0 && (
                            <div
                                style={{
                                    backgroundColor: "#fef3c7",
                                    border: "1px solid #fcd34d",
                                    borderRadius: "6px",
                                    padding: "12px",
                                    marginBottom: "16px",
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: "14px",
                                        color: "#92400e",
                                        fontWeight: "500",
                                        marginBottom: "8px",
                                    }}
                                >
                                    ⚠️ {regeneratingMeals.size} meal
                                    {regeneratingMeals.size > 1
                                        ? "s are"
                                        : " is"}{" "}
                                    currently being regenerated
                                </p>
                                <p
                                    style={{
                                        fontSize: "13px",
                                        color: "#92400e",
                                    }}
                                >
                                    Leaving this page will cancel these
                                    regenerations and any changes will NOT be
                                    applied.
                                </p>
                            </div>
                        )}

                        <p
                            style={{
                                fontSize: "16px",
                                color: "#6b7280",
                                marginBottom: "24px",
                            }}
                        >
                            {hasUnsavedChanges
                                ? "You have unsaved changes to your meal plan. Would you like to save them before leaving?"
                                : "Are you sure you want to leave? Active regenerations will be cancelled."}
                        </p>
                        <div
                            style={{
                                display: "flex",
                                gap: "12px",
                                justifyContent: "flex-end",
                            }}
                        >
                            <button
                                onClick={cancelNavigation}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#f3f4f6",
                                    color: "#374151",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    // Cancel all active regeneration tasks
                                    regenerationTaskIds.forEach((taskId) => {
                                        updateTask(taskId, {
                                            status: "failed",
                                            completedAt: new Date(),
                                            error: "Cancelled by user navigation",
                                        });
                                    });
                                    confirmNavigation();
                                }}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#ef4444",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                }}
                            >
                                {regeneratingMeals.size > 0
                                    ? "Cancel & Leave"
                                    : "Discard Changes"}
                            </button>
                            {hasUnsavedChanges && (
                                <button
                                    onClick={async () => {
                                        await handleSave();
                                        if (!saveError) {
                                            confirmNavigation();
                                        }
                                    }}
                                    disabled={saving}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: saving
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: saving ? "default" : "pointer",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {saving ? "Saving..." : "Save & Continue"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Shopping List Update Modal */}
            {isUpdatingShoppingList && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 10000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            padding: "40px",
                            textAlign: "center",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        }}
                    >
                        <div
                            style={{
                                display: "inline-block",
                                width: "60px",
                                height: "60px",
                                border: "4px solid #f3f4f6",
                                borderRadius: "50%",
                                borderTopColor: "#3b82f6",
                                animation: "spin 1s linear infinite",
                                marginBottom: "20px",
                            }}
                        />
                        <h3
                            style={{
                                fontSize: "20px",
                                fontWeight: "600",
                                color: "#1f2937",
                                marginBottom: "8px",
                            }}
                        >
                            Updating Shopping List
                        </h3>
                        <p
                            style={{
                                fontSize: "16px",
                                color: "#6b7280",
                            }}
                        >
                            Please wait while we update your shopping list...
                        </p>
                    </div>
                </div>
            )}

            {/* Add CSS for spinner animation */}
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>

            {/* Floating Navigation Sidebar - only show when viewing a day (not shopping list or model info) */}
            {!showShoppingList && !showModelInfo && showNavSidebar && (
                <div
                    style={{
                        position: "fixed",
                        right: "20px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        padding: "16px",
                        zIndex: 100,
                        minWidth: "180px",
                        maxHeight: "500px",
                        overflowY: "auto",
                    }}
                >
                    <div
                        style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#6b7280",
                            marginBottom: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                        }}
                    >
                        Quick Navigation
                    </div>
                    
                    {/* Day Navigation */}
                    <div
                        style={{
                            display: "flex",
                            gap: "4px",
                            marginBottom: "16px",
                            flexWrap: "wrap",
                        }}
                    >
                        {mealPlanData.mealPlanDays.map((_, dayIndex) => (
                            <button
                                key={dayIndex}
                                onClick={() => {
                                    setCurrentDayIndex(dayIndex);
                                    // Scroll to top of content area when switching days
                                    const contentArea = document.querySelector('[style*="overflow-y: auto"]');
                                    if (contentArea) contentArea.scrollTop = 0;
                                }}
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    border: "1px solid",
                                    borderColor: dayIndex === currentDayIndex ? "#3b82f6" : "#e5e7eb",
                                    borderRadius: "6px",
                                    backgroundColor: dayIndex === currentDayIndex ? "#3b82f6" : "white",
                                    color: dayIndex === currentDayIndex ? "white" : "#6b7280",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: dayIndex === currentDayIndex ? "600" : "400",
                                    transition: "all 0.2s",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                onMouseEnter={(e) => {
                                    if (dayIndex !== currentDayIndex) {
                                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                                        e.currentTarget.style.borderColor = "#9ca3af";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (dayIndex !== currentDayIndex) {
                                        e.currentTarget.style.backgroundColor = "white";
                                        e.currentTarget.style.borderColor = "#e5e7eb";
                                    }
                                }}
                            >
                                {dayIndex + 1}
                            </button>
                        ))}
                    </div>
                    
                    <div
                        style={{
                            borderTop: "1px solid #e5e7eb",
                            paddingTop: "12px",
                            marginBottom: "8px",
                        }}
                    />
                    
                    {/* Meal Links with Recipe Names */}
                    {(["breakfast", "lunch", "dinner", "snack"] as const).map((mealType) => {
                        const mealTypeDisplay = mealType.charAt(0).toUpperCase() + mealType.slice(1);
                        const sectionId = `day-${currentDayIndex}-${mealType}`;
                        const mealSlot = currentDay[mealType] as { recipes: any[] };
                        const firstRecipe = mealSlot?.recipes?.[0];
                        const recipeName = firstRecipe?.name || "";
                        
                        return (
                            <button
                                key={mealType}
                                onClick={() => scrollToSection(sectionId)}
                                style={{
                                    display: "block",
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "8px 12px",
                                    marginBottom: "4px",
                                    border: "none",
                                    backgroundColor: activeSection === sectionId ? "#eff6ff" : "transparent",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    if (activeSection !== sectionId) {
                                        e.currentTarget.style.backgroundColor = "#f9fafb";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeSection !== sectionId) {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                    }
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "14px",
                                        color: activeSection === sectionId ? "#2563eb" : "#374151",
                                        fontWeight: activeSection === sectionId ? "500" : "400",
                                    }}
                                >
                                    {mealTypeDisplay === "Snack" ? "Snacks" : mealTypeDisplay}
                                </div>
                                {recipeName && (
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            color: activeSection === sectionId ? "#60a5fa" : "#9ca3af",
                                            marginTop: "2px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "140px",
                                        }}
                                        title={recipeName}
                                    >
                                        {recipeName}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
